import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MessageCircle, Users, User, Plus, Send } from 'lucide-react';
import { useMessage } from '@/hooks/use-message';
import { useAuth } from '@/hooks/use-auth';
import { ConversationView } from '@/components/messages/ConversationView';
import { AdminMessageComposer } from '@/components/messages/AdminMessageComposer';
import { NewMessageDialog } from '@/components/messages/NewMessageDialog';
import { format } from 'date-fns';
import { VirtualList } from '@/components/common/VirtualList';

export default function Messages() {
  const location = useLocation();
  const { user } = useAuth();
  const { conversations, activeConversationId, setActiveConversation, isLoading } = useMessage();
  const [showAdminComposer, setShowAdminComposer] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);

  // Reset to conversation list when navigating to /messages
  useEffect(() => {
    if (location.pathname === '/messages' && activeConversationId) {
      setActiveConversation(null);
    }
  }, [location.pathname]);

  const handleConversationClick = (conversationId: string) => {
    setActiveConversation(conversationId);
  };

  const handleBackToList = () => {
    setActiveConversation(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading messages...</p>
        </div>
      </div>
    );
  }

  if (activeConversationId) {
    return (
      <ConversationView 
        conversationId={activeConversationId} 
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div className="space-y-6 max-w-full min-w-0">
      <div className="max-w-full min-w-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Messages</h1>
            <p className="text-muted-foreground mt-2">
              Chat with your groups and teammates
            </p>
          </div>
          <TooltipProvider>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    onClick={() => setShowNewMessage(true)} 
                    className="gap-2"
                    aria-label="Start new conversation"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">New</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Start new conversation</p>
                </TooltipContent>
              </Tooltip>
              {user?.isAdmin && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={() => setShowAdminComposer(true)} 
                      variant="secondary" 
                      className="gap-2"
                      aria-label="Send broadcast message to groups"
                    >
                      <Send className="h-4 w-4" />
                      <span className="hidden sm:inline">Admin</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Send broadcast message</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        </div>
      </div>

      <div className="max-w-full min-w-0">
        <VirtualList
          items={conversations}
          itemHeight={84}
          height={520}
          renderItem={(conversation) => (
            <Card 
              key={conversation.id}
              className="hover:bg-accent/50 transition-colors cursor-pointer max-w-full mb-4"
              onClick={() => handleConversationClick(conversation.id)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3 max-w-full min-w-0">
                  <Avatar className="w-12 h-12 shrink-0">
                    {conversation.avatarUrl && <AvatarImage src={conversation.avatarUrl} alt={conversation.name} />}
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {conversation.avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 max-w-full">
                    <div className="flex items-center justify-between gap-2 max-w-full min-w-0">
                      <CardTitle className="text-base font-medium flex items-center gap-2 min-w-0 flex-1">
                        <span className="truncate">{conversation.name}</span>
                        {conversation.type === 'group' ? (
                          <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                        ) : (
                          <User className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                      </CardTitle>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {conversation.lastMessage ? format(conversation.lastMessage.timestamp, 'HH:mm') : ''}
                        </span>
                        {conversation.unreadCount > 0 && (
                          <Badge variant="default" className="text-xs min-w-[20px] h-5 flex items-center justify-center">
                            {conversation.unreadCount}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardDescription className="truncate">
                      {conversation.lastMessage?.content || 'No messages yet'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          )}
        />
      </div>

      {conversations.length === 0 && (
        <div className="text-center py-12">
          <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No messages yet</h3>
          <p className="text-muted-foreground">
            Start a conversation with your teammates or groups
          </p>
        </div>
      )}

      {/* Admin Message Composer */}
      <AdminMessageComposer 
        open={showAdminComposer}
        onOpenChange={setShowAdminComposer}
      />

      {/* New Message Dialog */}
      <NewMessageDialog
        open={showNewMessage}
        onOpenChange={setShowNewMessage}
      />
    </div>
  );
}
